import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import {
    useFloating,
    autoUpdate,
    useInteractions,
    useHover,
    useClientPoint,
    offset,
    shift,
    limitShift,
    flip,
} from '@floating-ui/react'
import { useIsTruncated } from '@/hooks/useIsTruncated/useIsTruncated'
import classNames from 'classnames'
import { TooltipFloater } from '@/components/atoms'

type TruncatableTextProps = {
    text: string
    tooltipClassname?: string
}

export const TruncatableText: React.FC<TruncatableTextProps> = (props) => {
    const { text, tooltipClassname } = props
    const [isOpen, setIsOpen] = useState(false)

    const { refs, floatingStyles, context } = useFloating({
        placement: 'right',
        whileElementsMounted: autoUpdate,
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [
            offset(16),
            shift({
                padding: 5,
                limiter: limitShift(),
            }),
            flip(),
        ],
    })

    const isTruncated = useIsTruncated(refs.reference)

    const hover = useHover(context)
    const clientPoint = useClientPoint(context)

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        clientPoint,
    ])

    const floatingClassnames = classNames(
        'transition-all z-[100] pointer-events-none',
        tooltipClassname,
        {
            'opacity-0': !isOpen,
        }
    )

    return (
        <>
            <div
                data-testid="truncatable-text-reference"
                ref={refs.setReference}
                {...getReferenceProps()}
                className="overflow-hidden text-ellipsis whitespace-nowrap"
            >
                {text}
            </div>

            {isTruncated &&
                createPortal(
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps()}
                        className={floatingClassnames}
                        data-testid="truncatable-text-tooltip"
                    >
                        <TooltipFloater>{text}</TooltipFloater>
                    </div>,
                    document.body
                )}
        </>
    )
}
